// ---------------------------------------------------------------------------
// SANITIZAÇÃO DO BLOCO `texto` — allowlist, e nada além dela.
//
// Por que isto não é paranoia: a landing do restaurante e a tela onde o número
// do CARTÃO é digitado moram na MESMA ORIGEM (`*.alfredo.food` e `/checkout`).
// Um `<script>` gravado num bloco de texto rodaria no mesmo document.origin do
// checkout. O Raio injeta `customCss` e id de pixel com
// `dangerouslySetInnerHTML` sem sanitização — esse é o defeito que este arquivo
// existe para não herdar.
//
// A sanitização acontece na GRAVAÇÃO, no servidor, e o valor guardado JÁ É o
// limpo. Quem renderiza não precisa confiar em ninguém: se algum dia outro
// caminho gravar direto no banco, o render continua exposto — por isso o
// alfredo-pedidos chama esta mesma função antes de materializar o HTML.
//
// Sem dependência (nada de DOMPurify): módulo compartilhado, e o parser aqui é
// deliberadamente burro — ele não tenta entender HTML mal formado, ele descarta.
// ---------------------------------------------------------------------------

/** Tags que sobrevivem. Nada que carregue recurso, execute ou posicione. */
const TAGS_PERMITIDAS = new Set([
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "ul",
  "ol",
  "li",
  "h2",
  "h3",
  "h4",
  "blockquote",
  "a",
  "span",
]);

/** Tags sem fechamento. */
const TAGS_VAZIAS = new Set(["br"]);

/** Atributos permitidos, por tag. Fora daqui, some. */
const ATRIBUTOS_PERMITIDOS: Record<string, string[]> = {
  a: ["href", "title"],
};

const PROTOCOLOS_DE_LINK = ["http:", "https:", "mailto:", "tel:"];

function escaparTexto(valor: string): string {
  return valor
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escaparAtributo(valor: string): string {
  return escaparTexto(valor).replace(/"/g, "&quot;");
}

function hrefSeguro(bruto: string): string | null {
  const valor = bruto.trim();
  if (!valor) return null;
  // Relativo é aceito e não passa por `new URL` (não teria base).
  if (valor.startsWith("/") || valor.startsWith("#")) return valor;
  try {
    const url = new URL(valor);
    return PROTOCOLOS_DE_LINK.includes(url.protocol) ? valor : null;
  } catch {
    return null;
  }
}

function lerAtributos(bruto: string): Record<string, string> {
  const atributos: Record<string, string> = {};
  const padrao = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/g;
  let achado: RegExpExecArray | null;
  while ((achado = padrao.exec(bruto)) !== null) {
    const nome = achado[1].toLowerCase();
    atributos[nome] = achado[3] ?? achado[4] ?? achado[5] ?? "";
  }
  return atributos;
}

/**
 * Devolve o HTML só com o que a allowlist permite. Tag não permitida some
 * (junto com o conteúdo dela, quando é `<script>`/`<style>`, porque aí o
 * conteúdo é código, não texto); tag permitida perde todo atributo fora da
 * lista; `<a>` sem href seguro vira `<span>`, para não perder o texto.
 */
export function sanitizarHtml(bruto: string): string {
  if (!bruto) return "";

  // O conteúdo de script/style/iframe é código: sai inteiro, não só a tag.
  let entrada = bruto.replace(
    /<(script|style|iframe|object|embed|template|noscript)\b[\s\S]*?<\/\1\s*>/gi,
    "",
  );
  // Comentário HTML some: `<!--[if]>` é caminho conhecido de contrabando.
  entrada = entrada.replace(/<!--[\s\S]*?-->/g, "");

  const saida: string[] = [];
  const abertas: string[] = [];
  const padraoTag = /<\/?([a-zA-Z][a-zA-Z0-9]*)((?:[^>"']|"[^"]*"|'[^']*')*)>/g;

  let posicao = 0;
  let achado: RegExpExecArray | null;

  while ((achado = padraoTag.exec(entrada)) !== null) {
    if (achado.index > posicao) {
      saida.push(escaparTexto(entrada.slice(posicao, achado.index)));
    }
    posicao = achado.index + achado[0].length;

    const tag = achado[1].toLowerCase();
    const fechamento = achado[0].startsWith("</");

    if (!TAGS_PERMITIDAS.has(tag)) continue;

    if (fechamento) {
      if (TAGS_VAZIAS.has(tag)) continue;
      const indice = abertas.lastIndexOf(tag);
      if (indice === -1) continue;
      // Fecha tudo que ficou aberto por dentro — HTML torto não vira aninhamento
      // torto na saída.
      while (abertas.length > indice) {
        saida.push(`</${abertas.pop()}>`);
      }
      continue;
    }

    if (TAGS_VAZIAS.has(tag)) {
      saida.push(`<${tag} />`);
      continue;
    }

    const brutos = lerAtributos(achado[2] ?? "");
    const permitidos = ATRIBUTOS_PERMITIDOS[tag] ?? [];
    const partes: string[] = [];
    let tagFinal = tag;

    if (tag === "a") {
      const href = hrefSeguro(brutos.href ?? "");
      if (!href) {
        // Link com `javascript:` perde o link, não o texto.
        tagFinal = "span";
      } else {
        partes.push(`href="${escaparAtributo(href)}"`);
        // Link de conteúdo do dono sempre abre fora e sem passar o referrer.
        partes.push('target="_blank"', 'rel="noopener noreferrer nofollow"');
        if (brutos.title) partes.push(`title="${escaparAtributo(brutos.title)}"`);
      }
    } else {
      for (const nome of permitidos) {
        if (brutos[nome] !== undefined) {
          partes.push(`${nome}="${escaparAtributo(brutos[nome])}"`);
        }
      }
    }

    abertas.push(tagFinal);
    saida.push(partes.length ? `<${tagFinal} ${partes.join(" ")}>` : `<${tagFinal}>`);
  }

  if (posicao < entrada.length) {
    saida.push(escaparTexto(entrada.slice(posicao)));
  }
  while (abertas.length) {
    saida.push(`</${abertas.pop()}>`);
  }

  return saida.join("").trim();
}
